/**
 * PII-Scrub-Helper für User-Free-Text vor Übermittlung an externe
 * Sub-Processors (GitHub, Anthropic, etc.).
 *
 * Konvention: deterministische Regex-Ersetzung. Reihenfolge wichtig, weil
 * Patterns überlappen können (z.B. Phone-Regex frisst Datum wenn nicht
 * vorgestaffelt).
 *
 * Hinweis: Pseudonymisierung über Account-Slug bleibt – Daten sind nach
 * Scrub noch personenbezogen i.S.v. DSGVO Erwägungsgrund 26 wenn der
 * Verantwortliche (Locari) den Schlüssel hat. Risiko-Score sinkt aber
 * von MED auf LOW. Siehe `docs/superpowers/specs/2026-05-13-legal-update-locari.md`.
 */

// Email-Pattern: Standard RFC-5322-light, kein perfekt-strict aber praxis-tauglich.
const EMAIL_RE = /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g;

// IBAN: 2 Buchstaben Country-Code + 2 Check-Digits + 10–30 alphanumeric (DE: 22 chars).
const IBAN_RE = /\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/g;

// Geburtsdaten: dd.mm.yyyy (DE) ODER yyyy-mm-dd (ISO).
// Bereich 19xx/20xx → fängt 1900–2099 ab.
const DOB_RE =
  /\b(?:0?[1-9]|[12]\d|3[01])\.(?:0?[1-9]|1[0-2])\.(?:19|20)\d{2}\b|\b(?:19|20)\d{2}-(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[12]\d|3[01])\b/g;

// DE-PLZ + Stadtname: 5 Ziffern gefolgt von Capitalized-Wort.
const DE_POSTAL_RE = /\b\d{5}\s+[A-ZÄÖÜ][a-zäöüß-]+/g;

// DE-Straße + Hausnummer: Straßen-/weg-/allee-/etc. + Hausnummer.
const DE_STREET_RE =
  /\b[A-ZÄÖÜ][a-zäöüß]+(?:str(?:aße|\.)|weg|allee|platz|gasse|ring)\.?\s+\d+[a-z]?/gi;

// Telefon: international (+49…) oder national (030 12345 / 0151-12345678).
// Mindestens 7 Ziffern. Achtung: Reihenfolge nach DOB damit Datum nicht gefressen wird.
// 🔴 EINZIGE Quelle des Telefon-Musters: `security/error-sanitizer.ts` nutzt es
// mit (#783 — dort stand ein eigenes, das nur deutsche Nummern kannte).
export const PHONE_RE = /\b(?:\+\d{1,3}[ -]?)?(?:\d[ -]?){7,14}\d\b/g;

/**
 * Scrubbt PII aus Free-Text. Deterministisch, idempotent (zweimal scrubben
 * ist identisch zu einmal).
 *
 * Reihenfolge: Email → IBAN → DOB → DE-PLZ → DE-Straße → Phone.
 * Phone als letztes, weil Phone-Regex aggressivste Zahlen-Sequenzen frisst.
 */
export function piiScrub(text: string): string {
  if (!text) return text;
  return text
    .replace(EMAIL_RE, '[EMAIL]')
    .replace(IBAN_RE, '[IBAN]')
    .replace(DOB_RE, '[GEBURTSDATUM]')
    .replace(DE_POSTAL_RE, '[PLZ ORT]')
    .replace(DE_STREET_RE, '[STRASSE NR]')
    .replace(PHONE_RE, '[PHONE]');
}

/**
 * Scrubbt alle String-Werte in einem jsonb-artigen Object rekursiv.
 * Verwendet für Bug-Report-`context`-Feld.
 */
export function piiScrubDeep<T>(value: T): T {
  if (typeof value === 'string') {
    return piiScrub(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => piiScrubDeep(v)) as T;
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = piiScrubDeep(v);
    }
    return result as T;
  }
  return value;
}

/**
 * Scrubbt einen Stack-Trace zeilenweise: Frame-Zeilen (`    at fn (datei:zeile:spalte)`)
 * bleiben unverändert, alle anderen Zeilen (Meldungszeile `Error: …`, mehrzeilige
 * Meldungen) laufen durch `piiScrub`. Die Meldung steht im Stack als erste Zeile
 * und trägt dieselbe PII wie `error.message`.
 */
export function piiScrubStack(stack: string): string {
  if (!stack) return stack;
  return stack
    .split('\n')
    .map((line) => (/^\s+at\s/.test(line) ? line : piiScrub(line)))
    .join('\n');
}
